import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Vector3 } from 'three';
import { getBoardSquareLabel } from './doorBoard';
import { getBoardTileInfo } from './boardTileInfo';
import { getIceTileIndices } from './boardConditions';
import { BOUNTY_TAG_REWARD } from './bountyTag';
import './BoardTileInspector.css';
import { usePopupTransition } from './popupTransition';

let nextInspectorId = 0;
const clamp = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(maximum, value));

const BoardTileInspector = forwardRef(function BoardTileInspector(
  {
    board = [],
    disabled = false,
    context,
    boardCondition,
    portalPair,
    bountyTagIndex,
    chainReactionReady,
  },
  ref
) {
  const [id] = useState(() => `coreboundTileInfo-${++nextInspectorId}`);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const cellsRef = useRef([]);
  const highlightPolygonsRef = useRef([]);
  const buttonsRef = useRef([]);
  const popoverRef = useRef(null);
  const selectedRef = useRef(null);
  const visibleIndex = disabled ? null : selectedIndex;
  const { close: closeDetails, popupClass } = usePopupTransition(
    () => setSelectedIndex(null),
    { open: visibleIndex !== null, transitionKey: visibleIndex }
  );
  selectedRef.current = visibleIndex;

  const positionPopover = useCallback(() => {
    const popover = popoverRef.current;
    const cell = cellsRef.current[selectedRef.current];
    if (!popover || !cell || cell.hidden || !rootRef.current) return;
    const root = rootRef.current.getBoundingClientRect();
    const tile = cell.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;
    const above = tile.top - root.top - height - 10;
    const below = tile.bottom - root.top + 10;
    popover.style.left = `${clamp(
      tile.left - root.left + (tile.width - width) / 2,
      8,
      root.width - width - 8
    )}px`;
    popover.style.top = `${clamp(
      above >= 8 ? above : below,
      8,
      root.height - height - 8
    )}px`;
  }, []);

  // Projection only moves native controls; it never changes React state per frame.
  useImperativeHandle(
    ref,
    () => {
      const points = Array.from({ length: 4 }, () => new Vector3());
      const corners = [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ];
      const lastBounds = new WeakMap();
      return {
        updateProjection(tiles, camera, mount) {
          if (!rootRef.current) return;
          const bounds = mount.getBoundingClientRect();
          const root = rootRef.current.getBoundingClientRect();
          tiles.forEach(({ tile }, index) => {
            const cell = cellsRef.current[index];
            if (!cell) return;
            const { width, height, depth } = tile.geometry.parameters;
            points.forEach((point, corner) => {
              point.set(
                (corners[corner][0] * width) / 2,
                height / 2,
                (corners[corner][1] * depth) / 2
              );
              point.applyMatrix4(tile.matrixWorld).project(camera);
              point.x =
                (point.x * 0.5 + 0.5) * bounds.width + bounds.left - root.left;
              point.y =
                (-point.y * 0.5 + 0.5) * bounds.height + bounds.top - root.top;
            });
            const left = Math.min(...points.map((point) => point.x));
            const top = Math.min(...points.map((point) => point.y));
            const right = Math.max(...points.map((point) => point.x));
            const bottom = Math.max(...points.map((point) => point.y));
            cell.hidden =
              points.some((point) => point.z < -1 || point.z > 1) ||
              right <= 0 ||
              bottom <= 0 ||
              left >= root.width ||
              top >= root.height ||
              right - left < 1 ||
              bottom - top < 1;
            if (cell.hidden) return;
            const signature = points
              .map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`)
              .join(':');
            if (lastBounds.get(cell) === signature) return;
            lastBounds.set(cell, signature);
            cell.style.left = `${left}px`;
            cell.style.top = `${top}px`;
            cell.style.width = `${right - left}px`;
            cell.style.height = `${bottom - top}px`;
            const projectedPoints = points.map((point) => ({
              x: ((point.x - left) / (right - left)) * 100,
              y: ((point.y - top) / (bottom - top)) * 100,
            }));
            cell.style.clipPath = `polygon(${projectedPoints
              .map(({ x, y }) => `${x}% ${y}%`)
              .join(', ')})`;
            highlightPolygonsRef.current[index]?.setAttribute(
              'points',
              projectedPoints.map(({ x, y }) => `${x},${y}`).join(' ')
            );
          });
          positionPopover();
        },
        updateFallback(tiles) {
          if (!rootRef.current) return;
          const root = rootRef.current.getBoundingClientRect();
          tiles.forEach((tile, index) => {
            const cell = cellsRef.current[index];
            if (!cell) return;
            const bounds = tile.getBoundingClientRect();
            cell.hidden = bounds.width === 0 || bounds.height === 0;
            lastBounds.delete(cell);
            cell.style.left = `${bounds.left - root.left}px`;
            cell.style.top = `${bounds.top - root.top}px`;
            cell.style.width = `${bounds.width}px`;
            cell.style.height = `${bounds.height}px`;
            cell.style.clipPath = 'none';
            highlightPolygonsRef.current[index]?.setAttribute(
              'points',
              '0,0 100,0 100,100 0,100'
            );
          });
          positionPopover();
        },
      };
    },
    [positionPopover]
  );

  useEffect(() => {
    if (disabled) setSelectedIndex(null);
  }, [disabled]);

  useLayoutEffect(() => {
    if (visibleIndex !== null) positionPopover();
  }, [visibleIndex, positionPopover]);

  useEffect(() => {
    if (visibleIndex === null) return undefined;
    const dismissOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closeDetails();
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    return () =>
      document.removeEventListener('pointerdown', dismissOutside, true);
  }, [visibleIndex, closeDetails]);

  const dismiss = () => {
    closeDetails();
    buttonsRef.current[visibleIndex]?.focus({ preventScroll: true });
  };
  const onTileKeyDown = (event, index) => {
    const row = Math.floor(index / 5);
    const column = index % 5;
    const next = {
      ArrowUp: Math.max(0, row - 1) * 5 + column,
      ArrowDown: Math.min(4, row + 1) * 5 + column,
      ArrowLeft: row * 5 + Math.max(0, column - 1),
      ArrowRight: row * 5 + Math.min(4, column + 1),
      Home: event.ctrlKey ? 0 : row * 5,
      End: event.ctrlKey
        ? board.length - 1
        : Math.min(board.length - 1, row * 5 + 4),
    }[event.key];
    if (next === undefined || !board[next]) return;
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex(next);
    setSelectedIndex(next);
    buttonsRef.current[next]?.focus({ preventScroll: true });
  };

  const square = board[visibleIndex];
  const info = getBoardTileInfo(square, context || {});
  const notes = [...(info?.notes || [])];
  if (getIceTileIndices(boardCondition).includes(visibleIndex)) {
    notes.push('Ice lane: the die slides farther across this tile.');
  }
  if (Number.isInteger(visibleIndex) && visibleIndex === bountyTagIndex) {
    notes.push(
      `Bounty marked: land here and clear the room to earn +${BOUNTY_TAG_REWARD} bonus shards.`
    );
  }
  if (
    Number.isInteger(visibleIndex) &&
    (visibleIndex === portalPair?.entryIndex ||
      visibleIndex === portalPair?.exitIndex)
  ) {
    notes.push(
      portalPair.armed
        ? 'Portal pair: cross either portal while airborne to emerge from the other with your motion intact. The final landing determines the tile effect.'
        : 'Portal pair: these linked portals already moved the die during this throw.'
    );
  }
  if (chainReactionReady) {
    notes.push(
      'Chain Reaction is armed: the next non-Power Up landing and its up, right, down, and left neighbors activate once each. Direct Power Up landings preserve the charge through relaunches.'
    );
  }

  return (
    <div
      ref={rootRef}
      className="coreboundTileInspector"
      inert={disabled ? '' : undefined}
      onBlur={(event) => {
        if (
          event.relatedTarget &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          closeDetails();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && visibleIndex !== null) {
          event.preventDefault();
          event.stopPropagation();
          dismiss();
        }
      }}
    >
      <p id={`${id}-instructions`} className="coreboundTileInspectInstructions">
        Use the arrow keys to explore board tiles. Press Enter or Space for
        details, and Escape to close.
      </p>
      <div
        role="grid"
        aria-label="Inspect game board tiles"
        aria-describedby={`${id}-instructions`}
        aria-rowcount={5}
        aria-colcount={5}
      >
        {Array.from({ length: Math.ceil(board.length / 5) }, (_, row) => (
          <div key={row} role="row">
            {board.slice(row * 5, row * 5 + 5).map((tile, column) => {
              const index = row * 5 + column;
              const selected = visibleIndex === index;
              return (
                <div
                  key={index}
                  ref={(element) => {
                    cellsRef.current[index] = element;
                  }}
                  className="coreboundTileInspectCell"
                  role="gridcell"
                  aria-selected={selected}
                  hidden
                >
                  <button
                    ref={(element) => {
                      buttonsRef.current[index] = element;
                    }}
                    type="button"
                    className="coreboundTileInspectButton"
                    tabIndex={activeIndex === index ? 0 : -1}
                    disabled={disabled}
                    aria-label={`Row ${row + 1}, column ${
                      column + 1
                    }: ${getBoardSquareLabel(tile)}. Show tile details.`}
                    aria-haspopup="dialog"
                    aria-expanded={selected}
                    aria-controls={selected ? `${id}-popover` : undefined}
                    aria-describedby={
                      selected ? `${id}-description` : undefined
                    }
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={(event) => onTileKeyDown(event, index)}
                    onClick={() =>
                      setSelectedIndex((current) =>
                        current === index ? null : index
                      )
                    }
                  />
                  <svg
                    ref={(element) => {
                      highlightPolygonsRef.current[index] = element;
                    }}
                    className="coreboundTileInspectHighlight"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <polygon points="0,0 100,0 100,100 0,100" />
                  </svg>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {info && (
        <section
          ref={popoverRef}
          id={`${id}-popover`}
          className={`coreboundTileInfoPopover ${popupClass}`}
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-description`}
          style={{ '--tile-info-color': info.color }}
        >
          <header>
            <h3 id={`${id}-title`}>{info.name}</h3>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close tile details"
            >
              ×
            </button>
          </header>
          <p id={`${id}-description`} aria-live="polite">
            {info.description}
          </p>
          {info.example && (
            <p className="coreboundTileInfoExample">{info.example}</p>
          )}
          {notes.length > 0 && (
            <ul>
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
});

export default BoardTileInspector;
